<template>
  <div>
    <v-card class="segmentacao">
      <v-card-title class="py-0">
        <v-row>
          <v-col cols="9">
            <span class="bold table-card-title">
              <slot name="title">Minhas campanhas</slot>
            </span>
          </v-col>

          <v-col cols="3">
            <mf-search v-model="search" class="search-segment" label="Buscar por texto ou marcadores" hide-details />
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text class="px-0">
        <v-data-table
          :headers="headers"
          :loading="$apollo.queries.segments.loading"
          :options.sync="options"
          :items="items"
          :server-items-length="records"
          hide-details
          item-key="_id"
          :footer-props="{ itemsPerPageOptions: [5, 10, 15] }"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>
                <v-tooltip v-model="showTooltipTitle[item._id]" max-width="250" top content-class="truncated-tooltip">
                  <template v-slot:activator="{ on }">
                    <a class="link text-wrap" v-on="on" @click="redirect(item._id)">{{ item.name }}</a>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
              </td>
              <td>
                <v-tooltip v-model="showTooltipDescription[item._id]" max-width="250" top content-class="truncated-tooltip">
                  <template v-slot:activator="{ on }">
                    <span class="text-wrap" v-on="on">{{ item.description }}</span>
                  </template>
                  <span>{{ item.description }}</span>
                </v-tooltip>
              </td>
              <td class="">
                <unit-tags
                  :unit-tags="item.tags"
                  :limitTags="10"
                  :availableTags="tagsList"
                  @change="updateSegmentTags($event, item._id, item.tags)"
                  @delete-tag="deleteSegmentTag($event, item._id)"
                  @update-tag-name="updateSegmentTagName($event, item._id)"
                />
              </td>
              <td>{{ item.updated_at | data }}</td>
              <td class="text-right">
                <v-progress-circular v-if="item.updating" indeterminate color="primary" size="20" />
                <span v-else>{{ item.clients_qty | localeString(0) }}</span>
              </td>
              <td>{{ dateToMinutesAgo(item.clients_refreshed_at) }}</td>
              <td>
                <tooltip align="top" :message="!isIndustry ? tooltipUpdate : tooltipIndustryMessage">
                  <mf-button
                    type="icon"
                    icon="cached"
                    outlined
                    size="sm"
                    :disabled="(updating && !item.updating) || isIndustry"
                    :class="{ rotating: item.updating }"
                    @click="updateSegments(item)"
                  />
                </tooltip>
              </td>
              <td class="text-right">
                <v-menu bottom left class="segmento-menu" :close-on-content-click="false">
                  <template v-slot:activator="{ on }">
                    <mf-button color="cinza-0" icon="more_vert" type="icon" :disabled="updating" v-on:click.native="on.click" />
                  </template>
                  <v-list>
                    <export-segment v-if="item.clients_qty > 0" :segment="item" @modal-open="updateSegments(item)" />
                    <v-list-item @click="redirect(item._id)">
                      <v-list-item-title>{{ isIndustry ? 'Visualizar' : 'Editar' }}</v-list-item-title>
                    </v-list-item>
                    <tooltip align="left" :disabled="!isIndustry" :message="tooltipIndustryMessage">
                      <clone-segment :disabled="isIndustry" :segment-id="item._id" />
                    </tooltip>
                    <tooltip align="left" :disabled="!isIndustry" :message="tooltipIndustryMessage">
                      <v-list-item :disabled="isIndustry" @click="remover(item._id, item.campaign_qty, item.name)">
                        <v-list-item-title>Excluir</v-list-item-title>
                      </v-list-item>
                    </tooltip>
                    <v-list-item @click="openLogsModal(item._id)">
                      <v-list-item-title>Logs</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <logs-modal v-model="logsDialog" tags="SEGMENTS" logTitle="segmentação" :idItem="segmentationId" />
  </div>
</template>

<script>
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import ExportSegment from '@/modules/clientes/segmentation/views/components/ExportSegment.vue'
import { mapWaitingActions } from 'vue-wait'
import CloneSegment from '@/modules/clientes/segmentation/views/components/CloneSegment.vue'
import { SEGMENTS, MUTATION_TAGS_SEGMENTS } from '../../graphql'
import { dateToMinutesAgo } from '@/config/helpers.js'
import { DateTime } from 'luxon'
import Tooltip from '@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip.vue'
import UnitTags from '@/modules/configuracoes/lojas/views/components/UnitTags.vue'

export default {
  components: {
    ExportSegment,
    CloneSegment,
    LogsModal,
    Tooltip,
    UnitTags,
  },
  filters: {
    data(value) {
      return DateTime.fromISO(value).toFormat('dd/MM/yyyy | HH:mm')
    },
  },
  props: {
    isIndustry: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    segmentationId: '',
    search: '',
    logsDialog: false,
    updating: false,
    dateToMinutesAgo: dateToMinutesAgo,
    options: {},
    records: null,
    items: [],
    showTooltipTitle: {},
    showTooltipDescription: {},
    tooltipIndustryMessage: 'Essa segmentação faz parte de uma oferta de trade e não pode ser editada',
    tooltipUpdate: 'Atualizar',
  }),
  computed: {
    headers() {
      return [
        {
          text: 'Título',
          value: 'name',
          width: '20%',
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '20%',
        },
        {
          text: 'Marcadores',
          width: '23%',
          sortable: false,
        },
        {
          text: 'Última alteração',
          value: 'updated_at',
          width: '15%',
        },
        {
          text: 'Clientes',
          align: 'right',
          value: 'clients_qty',
          width: '10%',
        },
        {
          text: 'Atualizado',
          align: 'left',
          value: 'clients_refreshed_at',
          width: '10%',
        },
        {
          text: '',
          value: '',
          width: '1%',
          sortable: false,
        },
        {
          text: '',
          align: 'right',
          sortable: false,
          width: '1%',
        },
      ]
    },
    refreshedSegments() {
      return this.$store.getters.refreshedSegments
    },
    segmentsUpdating() {
      return this.$store.getters.updatingSegmentsIds
    },
    deletedTag() {
      return this.$store.getters.segmentationDeletedTag
    },
    updatedTag() {
      return this.$store.getters.segmentationUpdatedTag
    },
    tagsList() {
      return this.$store.getters.tagsList
    },
  },
  apollo: {
    segments: {
      query: SEGMENTS,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          page: this.options.page || 1,
          pageSize: this.options.itemsPerPage || 10,
          search: this.search,
          sort: this.options.sortBy,
          sortDirection: this.options.sortDesc && this.options.sortDesc.map((direction) => (direction ? 'DESC' : 'ASC')),
          isIndustry: this.isIndustry,
        }
      },
      update({ segments }) {
        if (!segments) return
        this.records = segments.records
        this.items = segments.segments.map((item) => {
          return {
            ...item,
            updating: false,
          }
        })
        this.$store.dispatch('setTags', segments.tags)
      },
    },
  },
  watch: {
    search() {
      this.options.page = 1
    },
    segmentsUpdating(value) {
      this.updating = value.length > 0
      this.items.forEach((item) => {
        if (value.includes(item._id)) {
          item.updating = true
        } else {
          item.updating = false
        }
      })
    },
    refreshedSegments(refreshedSegments) {
      this.items.forEach((item) => {
        const index = refreshedSegments.findIndex((refreshedSegment) => refreshedSegment._id === item._id)
        if (index > -1) {
          item.clients_refreshed_at = refreshedSegments[index].clients_refreshed_at
          item.clients_qty = refreshedSegments[index].clients_qty
          item.updating = false
        }
      })
      this.updating = false
    },
  },
  methods: {
    ...mapWaitingActions({
      deleteSegment: 'delete-segment',
    }),
    redirect(id) {
      if (this.isIndustry) {
        this.$router.push({ name: 'visualizar-segmentacao-trade', params: { id: id } }).catch(() => {})
      } else {
        this.$router.push({ name: 'editar-segmentacao', params: { id: id } }).catch(() => {})
      }
    },
    async remover(id, campaignQty, name) {
      if (campaignQty > 0) {
        this.$alert({
          title: 'Aviso',
          message: 'Não é possível excluir este segmento pois ele está incluído em alguma campanha.',
        })
        return
      }
      const confirmed = await this.$confirm({
        title: 'Remover segmentação',
        message: `Tem certeza que deseja remover a segmentação "${name}" permanentemente?`,
        confirmText: 'Remover',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)

      if (!confirmed) {
        return
      }

      this.$emit('loading-action', true)
      await this.deleteSegment(id)
        .then(() => {
          this.$apollo.queries.segments.refetch()
          this.$snackbar({
            message: 'Segmentação removida com sucesso!',
            snackbarColor: 'primary',
          })
        })
        .catch((err) => {
          console.log(err)
          this.$snackbar({
            message: 'Erro ao remover segmentação!',
            snackbarColor: 'error',
          })
        })
      this.$emit('loading-action', false)
    },
    updateSegments(item) {
      if (item.updating) return
      item.updating = true
      this.updating = true
      this.$store.dispatch('updateSegments', [item._id]).catch((error) => {
        console.log(error)
        this.$snackbar({
          message: 'Não foi possível atualizar o segmento, tente novamente mais tarde ou entre em contato com o suporte',
          snackbarColor: 'error',
        })
        item.updating = false
        this.updating = false
      })
    },
    openLogsModal(id) {
      this.segmentationId = id
      this.logsDialog = true
    },
    sanitizeTag(tag, action) {
      const { __typename, ...newTag } = tag
      return {
        oldName: null,
        ...newTag,
        updateAction: action,
      }
    },
    async updateSegmentTags(tagsToUpdate, segmentId, itemTags) {
      try {
        const currentTagNames = new Set((itemTags || []).map((tag) => tag.name))
        const newTagNames = new Set((tagsToUpdate || []).map((tag) => tag.name))

        const tagsToAdd = (tagsToUpdate || []).filter((tag) => !currentTagNames.has(tag.name))
        const tagsToRemove = (itemTags || []).filter((tag) => !newTagNames.has(tag.name))

        const addTags = tagsToAdd.map((tag) =>
          this.saveSegmentTags({
            _id: segmentId,
            tag: this.sanitizeTag(tag, 'INSERT'),
          })
        )

        const removeTags = tagsToRemove.map((tag) =>
          this.saveSegmentTags({
            _id: segmentId,
            tag: this.sanitizeTag(tag, 'DELETE_SEGMENT'),
          })
        )

        await Promise.all([...addTags, ...removeTags])
      } catch (error) {
        console.error('Erro ao atualizar segmentos:', error)
        throw error
      }
    },
    deleteSegmentTag(tagToDelete, segmentId) {
      this.$store.dispatch('deleteTag', tagToDelete)
      this.saveSegmentTags({
        _id: segmentId,
        tag: this.sanitizeTag(tagToDelete, 'DELETE_TAGS'),
      })
    },
    updateSegmentTagName(tagUpdated, segmentId) {
      this.$store.dispatch('updateTag', tagUpdated)
      this.saveSegmentTags({
        _id: segmentId,
        tag: this.sanitizeTag(
          {
            color: tagUpdated.tag.color,
            oldName: tagUpdated.oldName,
            name: tagUpdated.newName,
          },
          'UPDATE'
        ),
      })
    },
    async saveSegmentTags(data) {
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_TAGS_SEGMENTS,
          variables: { data },
        })

        this.$snackbar({
          message: 'Atualizado com sucesso!',
          snackbarColor: 'success',
        })

        this.$apollo.queries.segments.refetch()
        return true
      } catch (error) {
        console.error('Erro ao salvar segmento:', error)
        this.$snackbar({
          message: 'Ocorreu um erro, tente novamente mais tarde.',
          snackbarColor: 'error',
        })
        return false
      }
    },
  },
}
</script>

<style lang="scss">
.segmentacao {
  th {
    @extend .column-title;
  }

  .segmento-menu {
    i {
      color: rgba(0, 0, 0, 0.54) !important;
    }
  }

  .search-segment {
    margin-top: 0 !important;
    padding-top: 0;
  }

  .btn-update-text {
    margin-right: 8px;
    font-size: 14px;
    letter-spacing: 0.01em;
    text-transform: lowercase;
    &::first-letter {
      text-transform: capitalize;
    }
  }

  .animation {
    animation: rotate 2s infinite reverse;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  .rotating {
    animation: rotate 2s linear infinite;
  }

  .text-wrap {
    word-break: break-word;
    overflow-wrap: break-word;
  }

  @media (max-width: 1368px) {
    .v-data-table {
      td:nth-child(1),
      th:nth-child(1) {
        min-width: 240px !important;
        width: 240px !important;
      }

      td:nth-child(2),
      th:nth-child(2) {
        min-width: 240px !important;
        width: 240px !important;
      }
    }
  }
}
</style>
